import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  updateUserUserName,
  useEmployee,
  useEmployeeLoginData,
} from '~/data/employee';
import { AppDispatch } from '~/store';

export type useEmployeeDataOutput = {
  onSubmit: () => void;
  userName: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  canSubmit: boolean;
};

type PropsType = {
  onSubmitSuccess?: () => void;
};

export const useChangeUserName = (props: PropsType): useEmployeeDataOutput => {
  const { onSubmitSuccess } = props;
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  const { employee } = useEmployee();
  const [userName, setUserName] = useState<string>('');
  const [newUserName, setNewUserName] = useState<string>('');
  const canSubmit = userName !== newUserName;
  const {
    employeeAdminLoginData: employeeLoginData,
    loginDataLoading: isLoading,
    loginDataFailedLoading: failedToLoad,
  } = useEmployeeLoginData();

  useEffect(() => {
    if (!isLoading && !failedToLoad) {
      setUserName(employeeLoginData.fLoginUsername);
      setNewUserName(employeeLoginData.fLoginUsername);
    }
  }, [employeeLoginData.fLoginUsername, failedToLoad, isLoading]);

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = () => {
    if (userName !== newUserName) {
      dispatch(
        updateUserUserName({
          employeeId: employeeLoginData.fLoginEmpId,
          newUserName,
        }),
      )
        .then((result) => {
          if (!!onSubmitSuccess) {
            onSubmitSuccess();
          }
          enqueueSnackbar(t('laChangeUsernameOk'), {
            variant: 'success',
          });
        })
        .catch((error) => {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const changedUserName = e.target.value;
    const resolvedNewUserName: string =
      changedUserName !== ''
        ? changedUserName
        : `${employee.empBasicData.fEmpFirstName.toLowerCase()}.${employee.empBasicData.fEmpLastName.toLowerCase()}}`;
    setNewUserName(resolvedNewUserName);
  };

  return {
    onSubmit,
    userName: newUserName,
    handleChange,
    canSubmit,
  };
};
